import { Button, Grid, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Box } from '@mui/system';
import axios from 'axios';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import MultipleAndSingleImgValidateWithPreview from 'src/common/FileValidation/MultipleAndSingleImgValidateWithPreview';
import { TOKEN_KEY, url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import { FormValues } from './types';

type CreateImportantMessageProps = {
  handleClose: () => void;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
};

function CreateImportantMessage({ handleClose, setRefreshTrigger }: CreateImportantMessageProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: {
      status: 1,
    }
  });

  const [loading, setLoading] = useState<boolean>(false);
  const Toast = Toaster();
  const token = localStorage.getItem(TOKEN_KEY);

  const onSubmit = async (values: FormValues) => {
    try {
      if (!token) {
        Toast.fire({
          icon: 'error',
          title: 'Authentication token is missing. Please log in again.',
        });
        return;
      }

      setLoading(true);

      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('designation', values.designation);
      formData.append('message', values.message);
      formData.append('rank', String(values.order));
      formData.append('order', String(values.order));
      formData.append('status', String(values.status));

      if (values.image && values.image[0]) {
        formData.append('image', values.image[0]);
      }

      const { data } = await axios.post(
        `${url}/api/v1/admin/important-message`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data.success) {
        Toast.fire({
          icon: 'success',
          title: data.message || 'Important message created successfully!',
        });
        setRefreshTrigger((prev) => prev + 1);
        reset();
        handleClose();
      } else {
        Toast.fire({
          icon: 'error',
          title: data.message || 'Failed to create important message.',
        });
      }
    } catch (err: any) {
      Toast.fire({
        icon: 'error',
        title: `${err?.response?.data?.message || 'Something went wrong'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} sx={{  p: 1 }}>
      <Grid container spacing={1}>
        {/* Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='name'
            label='Name'
            type='text'
            autoFocus
            variant='outlined'
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name', { required: 'Name is required' })}
          />
        </Grid>

        {/* Designation */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='designation'
            label='Designation'
            type='text'
            variant='outlined'
            error={!!errors.designation}
            helperText={errors.designation?.message}
            {...register('designation', { required: 'Designation is required' })}
          />
        </Grid>

        {/* Message */}
        <Grid item xs={12}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='message'
            label='Message'
            multiline
            rows={4}
            variant='outlined'
            error={!!errors.message}
            helperText={errors.message?.message}
            {...register('message', { required: 'Message is required' })}
          />
        </Grid>

        {/* Order */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='order'
            label='Display Order'
            type='number'
            inputProps={{ min: 1, step: 1 }}
            variant='outlined'
            error={!!errors.order}
            helperText={errors.order?.message}
            {...register('order', { required: 'Display order is required' })}
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant='outlined' margin='normal'>
            <InputLabel id='status-label'>Status</InputLabel>
            <Select
              labelId='status-label'
              id='status'
              label='Status'
              defaultValue={1}
              {...register('status')}
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Image Upload */}
        <Grid item xs={12}>
          <MultipleAndSingleImgValidateWithPreview
            register={register}
            clearErrors={clearErrors}
            setError={setError}
            errors={errors}
            label='Upload Image'
            fieldName='image'
            hideCustomLabel
            onlyImage
            mode='single'
            isRequired
          />
        </Grid>

        {/* Action Button */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disableElevation
            disabled={loading}
            sx={{ py: 1.5 }}
          >
            {loading ? 'Creating...' : 'Create Important Message'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CreateImportantMessage;
